import type { Customer, Supplier } from "@shared/types";
import { invoiceTotals, lineTotals, type LineInput } from "@shared/totals";
import { formatMoney, formatMoneyPlain, formatDateSk, formatQty } from "@shared/format";

export interface PreviewInvoice {
  number: string | null;
  issue_date: string;
  delivery_date: string;
  due_date: string;
  variable_symbol: string | null;
  constant_symbol?: string | null;
  specific_symbol?: string | null;
  payment_method: string;
  note?: string | null;
  currency: string;
}

export interface PreviewItem extends LineInput {
  name: string;
  unit: string;
}

interface Props {
  invoice: PreviewInvoice;
  items: PreviewItem[];
  supplier: Supplier | null;
  customer: Customer | null;
}

export function InvoicePreview({ invoice, items, supplier, customer }: Props) {
  const totals = invoiceTotals(items);
  return (
    <article class="preview">
      <header class="preview__header">
        <div>
          {supplier ? (
            <>
              <span class="paper__label">Dodávateľ</span>
              <h2 class="preview__supplier">{supplier.name}</h2>
              <p class="preview__line">
                {[
                  supplier.address_line1,
                  `${supplier.address_zip} ${supplier.address_city}`.trim(),
                  supplier.address_country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              <p class="preview__line preview__ids">
                {[
                  supplier.ico && `IČO: ${supplier.ico}`,
                  supplier.dic && `DIČ: ${supplier.dic}`,
                  supplier.ic_dph
                    ? `IČ DPH: ${supplier.ic_dph}`
                    : "Nie je platiteľom DPH",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </>
          ) : (
            <p class="muted">Dodávateľ — chýba (nastavte v Nastaveniach)</p>
          )}
        </div>
        <div class="preview__title">
          <p class="muted">Faktúra — daňový doklad</p>
          <h1>{invoice.number ?? "—"}</h1>
        </div>
      </header>

      <section class="preview__parties">
        <div>
          <span class="paper__label">Odberateľ</span>
          {customer ? (
            <>
              <p class="preview__line preview__line--strong">{customer.name}</p>
              <p class="preview__line">
                {[
                  customer.address_line1,
                  `${customer.address_zip ?? ""} ${customer.address_city ?? ""}`.trim(),
                  customer.address_country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              <p class="preview__line preview__ids">
                {[
                  customer.ico && `IČO: ${customer.ico}`,
                  customer.dic && `DIČ: ${customer.dic}`,
                  customer.ic_dph && `IČ DPH: ${customer.ic_dph}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </>
          ) : (
            <p class="muted">— vyberte odberateľa —</p>
          )}
        </div>
        <dl class="preview__meta">
          <dt>Dátum vystavenia</dt>
          <dd>{formatDateSk(invoice.issue_date)}</dd>
          <dt>Dátum dodania</dt>
          <dd>{formatDateSk(invoice.delivery_date)}</dd>
          <dt>Dátum splatnosti</dt>
          <dd>{formatDateSk(invoice.due_date)}</dd>
          <dt>Spôsob úhrady</dt>
          <dd>{invoice.payment_method}</dd>
          <dt>Variabilný symbol</dt>
          <dd>{invoice.variable_symbol ?? "—"}</dd>
          {invoice.constant_symbol && (
            <>
              <dt>Konštantný symbol</dt>
              <dd>{invoice.constant_symbol}</dd>
            </>
          )}
        </dl>
      </section>

      <div class="preview__items-wrap">
      <table class="preview__items">
        <thead>
          <tr>
            <th>Názov a popis položky</th>
            <th class="num">Počet</th>
            <th>Jedn.</th>
            <th class="num">Jedn. cena bez DPH</th>
            <th class="num">DPH</th>
            <th class="num">Celkom bez DPH</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={6} class="muted">
                — žiadne položky —
              </td>
            </tr>
          ) : (
            items.map((it, i) => {
              const { net } = lineTotals(it);
              return (
                <tr key={i}>
                  <td>{it.name || <span class="muted">—</span>}</td>
                  <td class="num">{formatQty(it.quantity)}</td>
                  <td>{it.unit}</td>
                  <td class="num">{formatMoneyPlain(it.unit_price_net)}</td>
                  <td class="num">{it.vat_rate} %</td>
                  <td class="num">{formatMoneyPlain(net)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      <section class="preview__totals">
        <table>
          <thead>
            <tr>
              <th>Sadzba DPH</th>
              <th class="num">Základ DPH</th>
              <th class="num">Výška DPH</th>
            </tr>
          </thead>
          <tbody>
            {totals.vatBreakdown.map((row) => (
              <tr key={row.rate}>
                <td>{row.rate} %</td>
                <td class="num">{formatMoneyPlain(row.base)}</td>
                <td class="num">{formatMoneyPlain(row.vat)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div class="preview__grand">
          <span>Celková suma</span>
          <strong>{formatMoney(totals.gross, invoice.currency)}</strong>
        </div>
      </section>

      {supplier?.iban && (
        <section class="preview__payment">
          <div class="preview__payment-qr">
            <span class="preview__paybysquare">
              <span class="muted">PAY</span> <strong>by square</strong>
            </span>
            <div class="preview__payment-qr-placeholder" aria-hidden="true" />
          </div>
          <dl class="preview__payment-details">
            <dt class="paper__label">Platba</dt>
            <dd class="preview__payment-bank">
              {supplier.bank_name || "—"}
            </dd>
            <dt>IBAN</dt>
            <dd>{formatIban(supplier.iban)}</dd>
            {supplier.swift && (
              <>
                <dt>SWIFT</dt>
                <dd>{supplier.swift}</dd>
              </>
            )}
            <dt>Variabilný symbol</dt>
            <dd>{invoice.variable_symbol ?? "—"}</dd>
            <dt>Dátum splatnosti</dt>
            <dd>{formatDateSk(invoice.due_date)}</dd>
            <dt>Suma na úhradu</dt>
            <dd class="preview__payment-amount">
              {formatMoney(totals.gross, invoice.currency)}
            </dd>
          </dl>
        </section>
      )}

      {supplier?.signature_image_b64 && (
        <div class="preview__signature-row">
          <div class="preview__signature">
            <img
              src={`data:image/png;base64,${supplier.signature_image_b64}`}
              alt="Podpis"
            />
            <p class="muted">Podpis a pečiatka</p>
          </div>
        </div>
      )}

      {invoice.note && (
        <section class="preview__note">
          <h3>Poznámka</h3>
          <p>{invoice.note}</p>
        </section>
      )}

      {supplier && (
        <footer class="preview__footer">
          <p>
            Vystavil: {supplier.name}
            {supplier.phone && <>{"  ·  "}{supplier.phone}</>}
            {supplier.website && <>{"  ·  "}{supplier.website}</>}
            {supplier.email && <>{"  ·  "}{supplier.email}</>}
          </p>
        </footer>
      )}
    </article>
  );
}

function formatIban(iban: string): string {
  const compact = iban.replace(/\s+/g, "");
  return compact.replace(/(.{4})/g, "$1 ").trim();
}
